/* Copyright © 2026 YOUR GROWTH 365 (Jonas Brössel) — Proprietary. See LICENSE in repo root. */
/* =============================================================================
   CORE / TSE-AMOUNTS — Brutto JE STEUERSATZ als Single-Source (S241 · HF-10)
   =============================================================================
   Eine Quelle für die Pro-Satz-Brutto-Beträge, die drei Verbraucher teilen:

     1. Bon-Renderer   (views/admin/receipt.js → computeOrderReceipt)
     2. TSE-Signatur   (core/tse-adapter.js → tse-sign-Body gross_7/19_cents)
     3. DSFinV-K/Z-Bon (Tagesabschluss-Aufteilung Netto je Satz)

   -------------------------------------------------------------------------
   WARUM (S240-Retest, Befund HF-10 — KRITISCH, vorbestehend)
   -------------------------------------------------------------------------
   Die Edge-Function rechnete die Pro-Satz-Beträge aus den GERUNDETEN Steuer-
   Cents zurück (`grossFromTax = round(tax/satz) + tax`). Empirisch (QR
   dekodiert):

     Order 2026-08-25-001   4,10 € (19 %)  → `Beleg^4.07_0.00_…^4.10:Bar`
     Order 2026-08-26-003   5,90 € ( 7 %)  → `Beleg^0.00_5.96_…^5.90:Bar`
     Order 2026-08-26-001  10,00 € (beide) → `Beleg^4.07_5.96_…^10.00:Bar`
                                              ↑ Σ 10,03 ≠ 10,00

   65 ct Steuer bei 19 % ⇒ Netto 342 ⇒ Brutto 407 statt 410. Die Rückrechnung
   trifft nur die SUMME, nie den echten positionsabgeleiteten Pro-Satz-Wert
   (dieselbe Falle wie S143c/S145 beim Bon-Netto). KassenSichV/DSFinV-K
   verlangen, dass die TSE-Prozessdaten die Belegbeträge exakt abbilden.

   -------------------------------------------------------------------------
   AUFLÖSUNGS-REIHENFOLGE (der Reihe nach, erste passende gewinnt)
   -------------------------------------------------------------------------
   0. `stored`  — die beim Bezahlen server-seitig berechneten und GESPEICHERTEN
                  Werte (`gross_7_cents`/`gross_19_cents`/`gross_0_cents`,
                  phase_174). Sie sind die autoritative Quelle: der Server hat
                  sie aus den Positionen gerechnet, als der Beleg entstand.
                  Der Grund für diesen Pfad ist der S241-Retest-Befund HF-12 —
                  `listForDayClose` lädt Belege OHNE Positionen, der Z-Bon fiel
                  darum auf Pfad 3 zurück (35,35/15,79 statt 35,27/15,87).
   1. `items`   — Σ Positions-Brutto je Satz (+ Liefergebühr in ihrem Satz).
                  Gilt, wenn die Summe das Order-Brutto EXAKT trifft. Das ist
                  der Normalfall und der einzige exakte Weg.
   2. `discount`— Rabattierte Order: Positionen tragen das VOR-Rabatt-Brutto,
                  `total_gross_cents` das NACH-Rabatt-Brutto. Rekonstruiert
                  über `applyDiscountExcludingDeals` — 1:1-Spiegel von
                  `mark_order_paid` (phase_132) / `pay_order_split`
                  (phase_133): Deals (customizations->_dealMeta) laufen voll
                  durch, nur der Nicht-Deal-Anteil je Satz wird gemindert.
                  Wird nur genommen, wenn das Ergebnis das Order-Brutto trifft.
   3. `tax`     — Letzter Ausweg ohne Positionen (Cockpit-Order ohne Snapshot,
                  Teilbeleg ohne mitgegebene Zeilen): Rückrechnung aus der
                  Steuer WIE BISHER, aber mit Residual-Korrektur, sodass
                  Σ Pro-Satz == Zahlbetrag. Ungenau je Satz (± wenige Cent),
                  aber niemals mehr inkonsistent zur Summe.

   Die INVARIANTE `gross7 + gross19 + gross0 == totalGrossCents` gilt in ALLEN
   drei Fällen — `assertVatSplitMatchesTotal` erzwingt sie vor dem Signieren.

   VORZEICHEN: Stornobelege tragen negative Order-Beträge; die Positions-
   Brutti sind positiv. Das Vorzeichen kommt aus `totalGrossCents` und wird auf
   die Positions-Summen gespiegelt. Die Storno-Konvention der Edge-Function
   (fiskaly `sign`-Multiplikator) bleibt davon unberührt.

   PURITY: keine DOM-/Storage-Zugriffe, kein Date.now()/Math.random().
   Positionen werden vom Aufrufer injiziert (der Adapter lädt sie ggf. nach).
============================================================================ */

import { applyDiscountExcludingDeals } from './discounts.js';

/* -------------------------------------------------------------------------- */
/* Feld-Leser (die Codebase reicht Orders in camelCase UND snake_case durch)   */
/* -------------------------------------------------------------------------- */

/** Erste definierte Zahl aus mehreren Feldnamen; sonst 0. */
function _num(obj, ...keys) {
  if (!obj) return 0;
  for (const k of keys) {
    if (obj[k] != null && obj[k] !== '') {
      const n = Number(obj[k]);
      if (Number.isFinite(n)) return n;
    }
  }
  return 0;
}

/** Brutto einer Positionszeile (line_total bevorzugt, sonst Einzelpreis × Menge). */
export function lineGrossCents(item) {
  if (!item) return 0;
  const line = _num(item, 'line_total_cents', 'lineTotalCents', 'totalCents');
  if (line) return line;
  const unit = _num(item, 'unit_price_cents', 'unitPriceCents');
  const qty  = _num(item, 'qty', 'quantity') || 1;
  return unit * qty;
}

/** Steuersatz einer Zeile. Default 7 (wie im Bon-Renderer seit jeher). */
export function lineTaxRate(item) {
  return _num(item, 'tax_rate', 'taxRate') === 19 ? 19 : 7;
}

/** Deal-Position? mark_order_paid/pay_order_split prüfen `customizations->'_dealMeta'`. */
export function isDealLine(item) {
  const c = item && (item.customizations || item.customizations_snapshot);
  return !!(c && typeof c === 'object' && c._dealMeta != null);
}

/**
 * S143 / Ticket 1 — Liefergebühr als eigener Pro-Satz-Betrag.
 *
 * Die Gebühr ist serverseitig (phase_109) bereits in `total_gross_cents` +
 * Steueraufschlüsselung gefaltet, steckt aber NICHT in `order_items`. Für die
 * Pro-Satz-Summe muss sie darum separat dazugerechnet werden. Quelle ist der
 * immutable Snapshot in `delivery_address` (fee_cents + fee_tax_rate, vom RPC
 * zum Bestellzeitpunkt geschrieben); Fallback 19 % für Bestand vor phase_109.
 *
 * (Bis S241 lag diese Funktion als `deliveryFeeReceiptInfo` in
 * views/admin/receipt.js — sie wird von Bon UND TSE gebraucht und gehört
 * darum in den Core. receipt.js re-exportiert sie unter dem alten Namen.)
 *
 * @param {object} order gemappte Order (camelCase) bzw. Roh-Row (snake_case)
 * @returns {{feeCents:number, taxRate:number}|null}
 */
export function deliveryFeeInfo(order) {
  if (!order) return null;
  const da = order.deliveryAddress || order.delivery_address;
  if (!da || typeof da !== 'object') return null;
  const feeCents = Number(da.fee_cents) || 0;
  if (feeCents <= 0) return null;
  const taxRate = (Number(da.fee_tax_rate) === 7) ? 7 : 19;
  return { feeCents, taxRate };
}

/* -------------------------------------------------------------------------- */
/* 1 — Positions-Pfad                                                          */
/* -------------------------------------------------------------------------- */

/**
 * Summiert Positions-Brutto je Steuersatz und rechnet die Liefergebühr in
 * ihren Satz ein. Liefert IMMER positive Beträge (Vorzeichen setzt der
 * Aufrufer aus dem Order-Brutto).
 *
 * @param {Array}  items    Positionszeilen (snake_case oder camelCase)
 * @param {object} feeInfo  optional aus `deliveryFeeInfo(order)`
 * @returns {{gross7:number, gross19:number}}
 */
export function grossPerRateFromItems(items, feeInfo) {
  let gross7 = 0, gross19 = 0;
  for (const it of (Array.isArray(items) ? items : [])) {
    if (lineTaxRate(it) === 19) gross19 += lineGrossCents(it);
    else                        gross7  += lineGrossCents(it);
  }
  if (feeInfo && feeInfo.feeCents > 0) {
    if (feeInfo.taxRate === 19) gross19 += feeInfo.feeCents;
    else                        gross7  += feeInfo.feeCents;
  }
  return { gross7, gross19 };
}

/**
 * Deal-/Nicht-Deal-Aufteilung je Satz — Eingabe für `applyDiscountExcludingDeals`.
 * Die Liefergebühr zählt zum Deal-Topf (läuft voll durch, Memory
 * `gg-discount-never-on-delivery-fee`).
 */
export function dealSplitFromItems(items, feeInfo) {
  let dealGross7 = 0, dealGross19 = 0, discountableGross7 = 0, discountableGross19 = 0;
  for (const it of (Array.isArray(items) ? items : [])) {
    const g = lineGrossCents(it);
    const is19 = lineTaxRate(it) === 19;
    if (isDealLine(it)) { if (is19) dealGross19 += g; else dealGross7 += g; }
    else                { if (is19) discountableGross19 += g; else discountableGross7 += g; }
  }
  if (feeInfo && feeInfo.feeCents > 0) {
    if (feeInfo.taxRate === 19) dealGross19 += feeInfo.feeCents;
    else                        dealGross7  += feeInfo.feeCents;
  }
  return { dealGross7, dealGross19, discountableGross7, discountableGross19 };
}

/* -------------------------------------------------------------------------- */
/* 3 — Steuer-Fallback (ohne Positionen)                                       */
/* -------------------------------------------------------------------------- */

/**
 * Rückrechnung aus den gerundeten Steuer-Cents — der ALTE Weg, aber mit
 * Residual-Korrektur: die Rundungsdifferenz zum Zahlbetrag landet auf dem
 * betragsmäßig größeren Satz, damit Σ Pro-Satz == Zahlbetrag gilt.
 *
 * Trägt der Beleg gar keine Steuer (theoretisch: reiner 0 %-Beleg; im
 * Bestand 0 Fälle, live geprüft 2026-08-26), wandert der volle Betrag in
 * einen 0 %-Topf statt willkürlich auf 7 oder 19 %.
 *
 * @returns {{gross7:number, gross19:number, gross0:number}}
 */
export function grossPerRateFromTax({ totalGrossCents, tax7Cents, tax19Cents }) {
  const total = Number(totalGrossCents) || 0;
  const t7    = Number(tax7Cents)  || 0;
  const t19   = Number(tax19Cents) || 0;

  if (t7 === 0 && t19 === 0) {
    return { gross7: 0, gross19: 0, gross0: total };
  }

  // brutto_satz = netto_satz + steuer_satz, netto_satz = round(steuer/satz).
  let gross7  = t7  !== 0 ? Math.round(t7  * 100 / 7)  + t7  : 0;
  let gross19 = t19 !== 0 ? Math.round(t19 * 100 / 19) + t19 : 0;

  const residual = total - (gross7 + gross19);
  if (residual !== 0) {
    if (Math.abs(gross7) >= Math.abs(gross19)) gross7 += residual;
    else                                       gross19 += residual;
  }
  return { gross7, gross19, gross0: 0 };
}

/* -------------------------------------------------------------------------- */
/* Öffentliche Auflösung                                                       */
/* -------------------------------------------------------------------------- */

/**
 * Brutto je Steuersatz für einen Beleg (Order, Teilbeleg oder Storno-Pseudo-
 * Order). Garantiert `gross7Cents + gross19Cents + gross0Cents ===
 * totalGrossCents`.
 *
 * @param {object} order  Order/Beleg (camelCase oder snake_case)
 * @param {{items?: Array, feeInfo?: object|null}} [opts]
 *        `items`   — Positionen explizit injizieren (bevorzugt vor
 *                    `order.items` und `order.preorderSnapshot.items`).
 *        `feeInfo` — Liefergebühr explizit setzen; `null` unterdrückt sie.
 *                    Nötig bei Teil-Beträgen einer Liefer-Order (Teil-Storno,
 *                    Teilbeleg): die Pseudo-Order erbt `deliveryAddress` per
 *                    Spread, die Gebühr gehört aber nicht in den Teilbetrag.
 * @returns {{gross7Cents:number, gross19Cents:number, gross0Cents:number,
 *           totalGrossCents:number, source:'stored'|'items'|'discount'|'tax'}}
 */
export function grossPerRateFromOrder(order, opts) {
  const o = order || {};
  const total = _num(o, 'totalGrossCents', 'total_gross_cents');
  const tax7  = _num(o, 'tax7Cents',  'tax7AmountCents',  'tax_7_amount_cents');
  const tax19 = _num(o, 'tax19Cents', 'tax19AmountCents', 'tax_19_amount_cents');

  // 0 — Gespeicherte Werte (phase_174). Sie gelten nur, wenn sie die Invariante
  // erfüllen: eine kaputte Zeile darf keine falsche Aufteilung durchreichen.
  const stored = storedSplit(o);
  if (stored && stored.gross7Cents + stored.gross19Cents + stored.gross0Cents === total) {
    return _split(stored.gross7Cents, stored.gross19Cents, stored.gross0Cents, total, 'stored');
  }

  const items   = resolveItems(o, opts);
  const feeInfo = (opts && Object.prototype.hasOwnProperty.call(opts, 'feeInfo'))
    ? opts.feeInfo
    : deliveryFeeInfo(o);
  const sign    = total < 0 ? -1 : 1;

  if (items.length) {
    // 1 — Positionen treffen das Order-Brutto exakt (Normalfall).
    //
    // Zwei Vorzeichen-Konventionen, beide real (Live-DB 2026-08-26 geprüft):
    //   a) Positionen tragen das Vorzeichen selbst — eine Korrektur-Order aus
    //      `create_order_correction` hat NEGATIVE `line_total_cents`, die exakt
    //      das negative Order-Brutto ergeben.
    //   b) Positionen sind positiv, der Beleg ist negativ — so baut der
    //      Teil-Storno seine Pseudo-Order (Zeilen aus `toCancel`).
    // Bei sign = +1 sind beide Zweige identisch, es gibt also keine
    // Mehrdeutigkeit; bei sign = −1 kann höchstens einer passen.
    const { gross7, gross19 } = grossPerRateFromItems(items, feeInfo);
    if (gross7 + gross19 === total) {
      return _split(gross7, gross19, 0, total, 'items');
    }
    if ((gross7 * sign) + (gross19 * sign) === total) {
      return _split(gross7 * sign, gross19 * sign, 0, total, 'items');
    }

    // 2 — Rabatt: Positionen sind VOR Rabatt, das Order-Brutto ist NACH Rabatt.
    const percent = _num(o, 'discountPercent', 'discount_percent');
    if (sign === 1 && percent > 0 && percent <= 100) {
      const d = applyDiscountExcludingDeals(dealSplitFromItems(items, feeInfo), percent);
      if (d.grossAfter === total) {
        return _split(d.gross7After, d.gross19After, 0, total, 'discount');
      }
    }
  }

  // 3 — Fallback aus der Steuer, mit Residual-Korrektur.
  const f = grossPerRateFromTax({ totalGrossCents: total, tax7Cents: tax7, tax19Cents: tax19 });
  return _split(f.gross7, f.gross19, f.gross0, total, 'tax');
}

/**
 * Gespeicherte Pro-Satz-Beträge eines Belegs (phase_174), oder null wenn der
 * Beleg sie nicht trägt (vor phase_174 bezahlt, oder noch unbezahlt).
 *
 * Bewusst streng: es zählt nur, wenn BEIDE Hauptsätze gesetzt sind. Ein
 * halb gefülltes Feld wäre schlimmer als gar keins, weil es den exakten
 * Positions-Pfad verdrängen würde.
 */
export function storedSplit(order) {
  const o = order || {};
  const g7  = o.gross7Cents  != null ? o.gross7Cents  : o.gross_7_cents;
  const g19 = o.gross19Cents != null ? o.gross19Cents : o.gross_19_cents;
  if (g7 == null || g19 == null) return null;
  const n7 = Number(g7), n19 = Number(g19);
  if (!Number.isFinite(n7) || !Number.isFinite(n19)) return null;
  const g0 = o.gross0Cents != null ? o.gross0Cents : o.gross_0_cents;
  const n0 = Number(g0);
  return {
    gross7Cents:  n7,
    gross19Cents: n19,
    gross0Cents:  Number.isFinite(n0) ? n0 : 0
  };
}

/** Positionen aus opts → order.items → preorderSnapshot (erste nicht-leere). */
export function resolveItems(order, opts) {
  if (opts && Array.isArray(opts.items) && opts.items.length) return opts.items;
  const o = order || {};
  if (Array.isArray(o.items) && o.items.length) return o.items;
  const snap = o.preorderSnapshot || o.preorder_snapshot;
  if (Array.isArray(snap) && snap.length) return snap;
  if (snap && Array.isArray(snap.items) && snap.items.length) return snap.items;
  return [];
}

function _split(gross7Cents, gross19Cents, gross0Cents, totalGrossCents, source) {
  return { gross7Cents, gross19Cents, gross0Cents, totalGrossCents, source };
}

/**
 * Erzwingt die KassenSichV-Invariante: die Summe der Pro-Satz-Beträge MUSS
 * dem Zahlbetrag entsprechen. Wird vor jeder Signatur geprüft — lieber ein
 * harter Fehler als eine Signatur mit stillem Cent-Drift.
 *
 * @throws {Error} bei Abweichung
 */
export function assertVatSplitMatchesTotal(split, totalGrossCents, label) {
  const s = split || {};
  const sum = (Number(s.gross7Cents) || 0)
            + (Number(s.gross19Cents) || 0)
            + (Number(s.gross0Cents) || 0);
  const total = Number(totalGrossCents) || 0;
  if (sum !== total) {
    throw new Error(
      `TSE-Betragsprüfung fehlgeschlagen${label ? ' (' + label + ')' : ''}: `
      + `Summe je Steuersatz ${(sum / 100).toFixed(2)} € ≠ Zahlbetrag `
      + `${(total / 100).toFixed(2)} €. Beleg wurde NICHT signiert.`
    );
  }
  return true;
}

/**
 * Formatiert die Pro-Satz-Beträge als BSI-TR-03153-`processData`:
 * `Beleg^<19%>_<7%>_<10,7%>_<5,5%>_<0%>^<Zahlbetrag>:<Bar|Unbar>`
 *
 * Reihenfolge der Sätze ist durch die Norm festgelegt (19 zuerst) — bestätigt
 * am Live-Payload der fiskaly-TSS (2026-08-26).
 */
export function buildProcessData(split, { totalGrossCents, paymentLabel = 'Bar', processType = 'Beleg' } = {}) {
  const eur = (c) => ((Number(c) || 0) / 100).toFixed(2);
  const s = split || {};
  const amounts = [
    eur(s.gross19Cents), eur(s.gross7Cents), '0.00', '0.00', eur(s.gross0Cents)
  ].join('_');
  return `${processType}^${amounts}^${eur(totalGrossCents)}:${paymentLabel}`;
}
